import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import commissionGroupsService from '../../common/api/commissionGroupsService';
import { LOADING_STATUS } from '../../common/constants';

export interface CommissionGroup {
  id: string;
  name: string;
}

interface CommissionGroupState {
  commissionGroupItems: CommissionGroup[];
  status: LOADING_STATUS.IDLE | LOADING_STATUS.LOADING | LOADING_STATUS.FAILED;
}

const initialState: CommissionGroupState = {
  commissionGroupItems: [],
  status: LOADING_STATUS.IDLE,
};

export const getCommissionGroups = createAsyncThunk(
  'commissionGroups/getCommissionGroups',
  async (siteId: string, { rejectWithValue }) => {
    try {
      return await commissionGroupsService.fetchCommissionGroups(siteId);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

const commissionGroupSlice = createSlice({
  name: 'commissionGroups',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getCommissionGroups.pending, state => {
        state.status = LOADING_STATUS.LOADING;
      })
      .addCase(getCommissionGroups.fulfilled, (state, action) => {
        state.status = LOADING_STATUS.IDLE;
        state.commissionGroupItems = action.payload;
      })
      .addCase(getCommissionGroups.rejected, (state, action) => {
        state.status = LOADING_STATUS.FAILED;
      });
  },
});

export default commissionGroupSlice.reducer;

export const selectCommissionGroupItems = (state: RootState) =>
  state.commissionGroups.commissionGroupItems;

// Memoized selector that only recompute their value when their input selectors have changed.
export const selectCommissionGroupDataTableItems = createSelector(
  selectCommissionGroupItems,
  commissionGroupItems =>
    commissionGroupItems.map(item => ({
      ItemId: item.id,
      Items: [item.name],
      Url: `/accommodations/commissiongroups/${item.id}`,
    })),
);

export const selectStatus = (state: RootState) => state.commissionGroups.status;
